import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Form from "forms/Form";
import { Project } from "types";
import { TrackingContext } from "context/Tracking";
import { useConfirmation } from "context/Confirmation";
import { useRiskGroups } from "pages/Risk/hooks/useRiskGroups";
import { Ex, FormCb } from "components/Risk/formdata";

import { isTimeoutError } from "./api";
import { validateDraft } from "./validateDraft";
import { WarningsPanel } from "./WarningsPanel";
import { ReasoningPanel } from "./ReasoningPanel";
import { StandardsAccessPanel } from "./StandardsAccessPanel";
import { AcceptGateModal } from "./AcceptGateModal";
import { RegenerateModal } from "./RegenerateModal";
import { GENERATE_PHASE_KEYS } from "./HazardShellForm";
import { GenerationLoadingView } from "./GenerationLoadingView";
import { useUpdateAIHazard } from "./hooks/useUpdateAIHazard";
import { useSaveAIHazardDraft } from "./hooks/useSaveAIHazardDraft";
import { useGenerateAIHazardDetail } from "./hooks/useGenerateAIHazardDetail";
import {
  buildAIDraftFormData,
  handleAIDraftGroupChange,
} from "./draftFormData";
import { AIDraftRisk, AIHazardShell, AIHazardStatus } from "./types";

// Maps the Formik form values (FormCb shape, same as the status-quo editor)
// back onto the AIDraftRisk wire shape for PATCH draft saves and the accept
// body. Mirrors useUpdateRisk's normalization (`|| 0` params, duplicated
// `assessment`, `applied_standards || []`), strips the form's junk
// informational fields and preserves the non-form draft fields (mitigation
// id/version/reasoning survive on the value objects since the editor seeds
// them via defaultValues).
export const buildDraftFromFormValues = (values: FormCb): AIDraftRisk => {
  const risk = values.risk as unknown as Record<string, any>;

  const assessment = {
    impact: risk.impact || 0,
    people_count: risk.people_count || 0,
    duration: risk.duration || 0,
    probability: risk.probability || 0,
    detectability: risk.detectability || 0,
  };

  return {
    readable_name: risk.readable_name || "",
    group_id: risk.group_id,
    origin_id: risk.origin_id || "",
    consequence_id: risk.consequence_id || "",
    phase_id: risk.phase_id || "",
    description: risk.description || "",
    protection_goal: risk.protection_goal || "",
    personal: risk.personal || "",
    ...assessment,
    assessment,
    // NEVER taken from the form/AI: the user decides fully_mitigated in the
    // Accept Gate only; drafts always carry false (plan §1.2 item 9).
    fully_mitigated: false,
    reasoning: risk.reasoning || "",
    mitigations: (values.mitigations || []).map((mitigation, index) => {
      const draftMitigation = mitigation as unknown as Record<string, any>;

      return {
        // Backend-minted per draft; manually added iterations get a local
        // placeholder until the next generation/accept re-mints them.
        id: draftMitigation.id || `draft-mit-${index + 1}`,
        version: draftMitigation.version || 1,
        type: draftMitigation.type || "",
        description: draftMitigation.description || "",
        pictogram: draftMitigation.pictogram || "",
        left_risk: draftMitigation.left_risk || "",
        reasoning: draftMitigation.reasoning || "",
        applied_standards:
          (draftMitigation.applied_standards as string[]) || [],
        impact: draftMitigation.impact || 0,
        people_count: draftMitigation.people_count || 0,
        duration: draftMitigation.duration || 0,
        probability: draftMitigation.probability || 0,
        detectability: draftMitigation.detectability || 0,
      };
    }),
  };
};

interface HazardDetailEditorProps {
  projectId: string;
  project?: Project;
  // status `generated` (or dismissed-with-draft), draft ≠ null — the page
  // only mounts this branch then.
  hazard: AIHazardShell;
  // Session group label → RiskGroupping label in the accept body.
  groupLabel: string;
}

// PHASE B (plan §1/§5): the status-quo risk editor (forms/Form + the CLONED
// buildRiskFormData structure) operating on the persisted AI draft. Autosave
// and explicit saves PATCH the draft into the AI session — never
// project.risks. "Save & review" opens the Accept Gate; Regenerate creates a
// persisted proposal for the diff view; Dismiss goes through the
// app-wide confirmation.
export const HazardDetailEditor = ({
  projectId,
  project,
  hazard,
  groupLabel,
}: HazardDetailEditorProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { riskGroups } = useRiskGroups();
  const { track } = useContext(TrackingContext);
  const { openConfirmation } = useConfirmation();
  const [formData, setFormData] = useState<Ex>();
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [gateState, setGateState] = useState<{
    show: boolean;
    draft: AIDraftRisk;
  } | null>(null);

  const saveDraftMutation = useSaveAIHazardDraft(projectId);
  const updateHazardMutation = useUpdateAIHazard(projectId);
  const generateMutation = useGenerateAIHazardDetail(projectId);

  const draft = hazard.draft!;
  const projectStandards = useMemo(
    () => project?.standards || [],
    [project]
  );

  // Validation of the PERSISTED draft for the on-page WarningsPanel; the
  // Accept Gate re-validates the final form values separately (plan §8).
  const validation = useMemo(
    () => validateDraft(draft, riskGroups || [], projectStandards),
    [draft, riskGroups, projectStandards]
  );

  const hasTrackedWarnings = useRef(false);
  useEffect(() => {
    if (!hasTrackedWarnings.current && validation.warnings.length > 0) {
      hasTrackedWarnings.current = true;
      track("ai risk: validation warnings", {
        codes: validation.warnings.map((warning) => warning.code),
      });
    }
  }, [validation, track]);

  useEffect(() => {
    if (!riskGroups || !riskGroups.length || !draft) {
      return;
    }

    // Defensive (plan §5): an invalid draft.group_id falls back to the first
    // risk group for FORM BUILDING only — the vocab_group hard error stays
    // visible in the WarningsPanel and blocks accept.
    const effectiveGroupId = riskGroups.some(
      (riskGroup) => riskGroup.riskGroup.name === draft.group_id
    )
      ? draft.group_id
      : riskGroups[0].riskGroup.name;

    const built = buildAIDraftFormData(
      riskGroups,
      (key: string) => t(key),
      effectiveGroupId,
      // Replicates Edit.tsx: one form item per existing mitigation.
      draft.mitigations?.length || 1
    );

    setFormData(built);
  }, [riskGroups, draft, t]);

  if (!formData) {
    return null;
  }

  // Group change rebuilds the per-group origin/consequence options — ALWAYS
  // on the stripped clone, never the module singleton (draftFormData.ts).
  const handleChange = (values: {}) => {
    const form = values as FormCb;
    const selectedRiskGroup = riskGroups?.find(
      (riskGroup) => riskGroup.riskGroup.name === form.risk.group_id
    );

    if (!selectedRiskGroup) {
      return;
    }

    setFormData(
      handleAIDraftGroupChange(
        selectedRiskGroup,
        (key: string) => t(key),
        formData
      )
    );
  };

  // No silent removal (plan §1.2 item 3): dismissal always goes through the
  // app-wide confirmation dialog, then back to the board loop.
  const handleDismiss = () => {
    openConfirmation(
      () =>
        updateHazardMutation.mutate(
          { hazardId: hazard.id, patch: { status: AIHazardStatus.dismissed } },
          { onSuccess: () => history.push(`/projects/${projectId}/risks/ai`) }
        ),
      {
        label: t("risks.ai.card.dismiss.confirm.label"),
        subline: t("risks.ai.card.dismiss.confirm.subline"),
      }
    );
  };

  const handleRegenerate = (regenerationContext: string) => {
    setShowRegenerateModal(false);

    // On success the hook merges the returned shell (persisted
    // pending_proposal) into the session cache and the detail page swaps
    // this editor for the DiffReviewPanel.
    generateMutation.mutate({
      hazardId: hazard.id,
      mode: "regenerate",
      name: hazard.name,
      description: hazard.description,
      user_context: hazard.user_context || "",
      regeneration_context: regenerationContext.trim() || undefined,
    });
  };

  const { mitigations, ...draftSansMitigations } = draft;

  return (
    <div className="mt-6 space-y-4">
      {/* AI provenance: risk-level + per-mitigation reasoning (read-only —
          reasoning is not a form field, plan §5). */}
      <ReasoningPanel
        reasoning={draft.reasoning || hazard.reasoning}
        mitigations={mitigations}
        defaultOpen={false}
      />

      <WarningsPanel
        validation={validation}
        generationWarnings={hazard.generation_warnings}
        skippedCategories={hazard.skipped_categories}
        defaultOpen={false}
      />

      <StandardsAccessPanel
        mitigations={mitigations}
        onRegenerate={() => setShowRegenerateModal(true)}
      />

      {/* The risk_image upload is stripped pre-accept (no resource_id yet) —
          images are added in the normal editor after acceptance. */}
      <p className="text-sm text-gray-500">{t("risks.ai.detail.imageHint")}</p>

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => setShowRegenerateModal(true)}
          disabled={generateMutation.isLoading}
          className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 disabled:opacity-50"
        >
          {t("risks.ai.detail.regenerate.cta")}
        </button>
        <button
          type="button"
          onClick={handleDismiss}
          disabled={updateHazardMutation.isLoading}
          className="text-sm font-medium text-gray-500 hover:text-red-600 disabled:opacity-50"
        >
          {t("risks.ai.card.dismiss.cta")}
        </button>
      </div>

      {generateMutation.isLoading && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-gray-900 bg-opacity-30">
          <div className="bg-white rounded-xl border border-gray-200 shadow-xl w-full max-w-sm mx-4">
            <GenerationLoadingView phaseKeys={GENERATE_PHASE_KEYS} />
          </div>
        </div>
      )}

      {generateMutation.isError && !generateMutation.isLoading && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <p className="text-sm text-red-700">
            {t(
              isTimeoutError(generateMutation.error)
                ? "risks.ai.errors.timeout"
                : "risks.ai.errors.generationFailed"
            )}
          </p>
        </div>
      )}

      <Form
        formData={formData}
        onChange={handleChange}
        saveText={t("risks.ai.detail.saveAndReview.cta")}
        defaultValues={{
          risk: draftSansMitigations,
          mitigations,
          _uploads: {},
        }}
        onAutosave={(v) =>
          saveDraftMutation.mutateAsync({
            hazardId: hazard.id,
            draft: buildDraftFromFormValues(v as FormCb),
            isAutoSave: true,
          })
        }
        // onStep/onAdd mitigation prefill copied from components/Risk/Edit.tsx
        // (copy, don't import — that file must stay untouched).
        onStep={(values: any, stepID, setFieldValue) => {
          if (
            stepID === 0 &&
            values.mitigations?.length &&
            !values.mitigations[0].impact &&
            !values.mitigations[0].duration &&
            !values.mitigations[0].probability &&
            !values.mitigations[0].people_count &&
            !values.mitigations[0].detectability
          ) {
            setFieldValue("mitigations[0].impact", values.risk.impact);
            setFieldValue("mitigations[0].duration", values.risk.duration);
            setFieldValue(
              "mitigations[0].probability",
              values.risk.probability
            );
            setFieldValue(
              "mitigations[0].people_count",
              values.risk.people_count
            );
            setFieldValue(
              "mitigations[0].detectability",
              values.risk.detectability
            );
          }
        }}
        onAdd={(values: any) => {
          const lastMitigation = values.mitigations?.length
            ? values.mitigations[values.mitigations.length - 1]
            : values.risk;

          return {
            impact: lastMitigation.impact,
            duration: lastMitigation.duration,
            probability: lastMitigation.probability,
            people_count: lastMitigation.people_count,
            detectability: lastMitigation.detectability,
          };
        }}
        // "Save & review" → Accept Gate with the FINAL form values; nothing
        // is persisted to project.risks outside the gate's accept endpoint.
        onSubmit={async (v) =>
          setGateState({ show: true, draft: buildDraftFromFormValues(v as FormCb) })
        }
      />

      {gateState && (
        <AcceptGateModal
          show={gateState.show}
          projectId={projectId}
          hazard={hazard}
          draft={gateState.draft}
          groupLabel={groupLabel}
          riskGroups={riskGroups || []}
          projectStandards={projectStandards}
          onClose={() =>
            setGateState((state) => state && { ...state, show: false })
          }
        />
      )}

      <RegenerateModal
        show={showRegenerateModal}
        onClose={() => setShowRegenerateModal(false)}
        onConfirm={handleRegenerate}
      />
    </div>
  );
};
